import React from "react";
import { generateAxiosInstance } from "../hooks/useAuthorizedRequest";
import { generateAxiosInstanceInstitute } from "../hooks/useAuthorizedRequestForInstitute";
import { generateAxiosInstanceSection } from "../hooks/useAuthorizedRequestForSection";
import { generateAxiosInstanceCommon } from "../hooks/useAuthorizedReuestCommon";
import Swal from "sweetalert2";

const authorizedAxios = generateAxiosInstance()
const authorizedAxiosInstitute = generateAxiosInstanceInstitute()
const authorizedAxiosSection = generateAxiosInstanceSection()
const authorizedAxiosCommon = generateAxiosInstanceCommon()

export function deleteItem(currPage, itemsPerPage, totalItems) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Calculate the start and end indexes of the current page
  const startIndex = (currPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Number of items on the current page
  const itemsOnCurrPage = endIndex - startIndex;

  if (itemsOnCurrPage === 1 && currPage > 1) {
    return currPage - 1;
  }

  return currPage; // Otherwise, stay on the same page
}


export function findIndexById(id, array) {
  for (let i = 0; i < array.length; i++) {
    if (array[i].id == id) {
      return i; // Return the index if ID matches
    }
  }
  return -1; // Return -1 if ID is not found
}

export function findPageNumber(array, id, itemsPerPage) {
  // Find the index of the item with the provided ID
  const index = array.findIndex(item => item.id === id);

  // Calculate the page number
  if (index !== -1) {
    const pageNumber = Math.floor(index / itemsPerPage) + 1;
    return pageNumber;
  } else {
    return -1; // ID not found in the array
  }
}

function parseCustomDateString(dateString) {
  const [datePart, timePart] = dateString.split(', ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [time, period] = timePart.split(' ');
  let [hours, minutes, seconds] = time.split(':').map(Number);

  if (period.toLowerCase() === 'pm' && hours < 12) {
    hours += 12;
  }
  if (period.toLowerCase() === 'am' && hours === 12) {
    hours = 0;
  }

  return new Date(year, month - 1, day, hours, minutes, seconds);
}


export function sortAscending(data, propertyPath, isGrade = false) {
  return data.sort((a, b) => {
    const nestedA = getNestedPropertyValue(a, propertyPath);
    const nestedB = getNestedPropertyValue(b, propertyPath);

    if (isGrade) {
      const valA = romanGradeToInt(nestedA);
      const valB = romanGradeToInt(nestedB);

      if (valA !== null && valB !== null) return valA - valB;
      if (valA !== null) return -1;
      if (valB !== null) return 1;
      return String(nestedA).localeCompare(String(nestedB));
    }

    // If both values are arrays, concatenate and compare
    if (Array.isArray(nestedA) && Array.isArray(nestedB)) {
      const concatenatedA = concatenateNestedValues(nestedA, propertyPath);
      const concatenatedB = concatenateNestedValues(nestedB, propertyPath);
      return concatenatedA.localeCompare(concatenatedB);
    }

    // Check if nestedA and nestedB are date strings
    if (typeof nestedA === 'string' && isDateString(nestedA) && typeof nestedB === 'string' && isDateString(nestedB)) {
      const dateA = parseCustomDateString(nestedA);
      const dateB = parseCustomDateString(nestedB);
      return dateA - dateB;
    }

    // If both are strings, compare them directly
    if (typeof nestedA === 'string' && typeof nestedB === 'string') {
      return nestedA.localeCompare(nestedB);
    }

    // If both are numbers, compare them directly
    if (typeof nestedA === 'number' && typeof nestedB === 'number') {
      return nestedA - nestedB;
    }

    // Default case: fallback to comparing values as they are
    return nestedA > nestedB ? 1 : nestedA < nestedB ? -1 : 0;
  });
}


export function sortDescending(data, propertyPath, isGrade = false) {
  return data.sort((a, b) => {
    const nestedA = getNestedPropertyValue(a, propertyPath);
    const nestedB = getNestedPropertyValue(b, propertyPath);

    if (isGrade) {
      const valA = romanGradeToInt(nestedA);
      const valB = romanGradeToInt(nestedB);

      if (valA !== null && valB !== null) return valB - valA;
      if (valB !== null) return 1;
      if (valA !== null) return -1;
      return String(nestedB).localeCompare(String(nestedA));
    }

    // Check if nestedA and nestedB are date strings
    const isDateA = isDateString(nestedA);
    const isDateB = isDateString(nestedB);

    if (isDateA && isDateB) {
      const dateA = parseCustomDateString(nestedA);
      const dateB = parseCustomDateString(nestedB);
      return dateB - dateA;
    }

    // If both are strings, compare them directly
    if (typeof nestedA === 'string' && typeof nestedB === 'string') {
      return nestedB.localeCompare(nestedA);
    }

    // If both are numbers, compare them directly
    if (typeof nestedA === 'number' && typeof nestedB === 'number') {
      return nestedB - nestedA;
    }

    // Additional logic for concatenating values dynamically
    if (Array.isArray(nestedA) && Array.isArray(nestedB)) {
      const concatenatedA = concatenateNestedValues(nestedA, propertyPath);
      const concatenatedB = concatenateNestedValues(nestedB, propertyPath);
      return concatenatedB.localeCompare(concatenatedA);
    }

    // Default case: fallback to comparing values as they are
    return nestedB > nestedA ? 1 : nestedB < nestedA ? -1 : 0;
  });
}

function concatenateNestedValues(array, propertyPath) {
  // Extract the final property to concatenate
  const finalProperty = propertyPath.split('.').slice(-1)[0];
  return array.map(item => getNestedPropertyValue(item, finalProperty)).join(', ');
}


function isDateString(dateString) {
  return /\d{1,2}\/\d{1,2}\/\d{4}, \d{1,2}:\d{2}:\d{2} (am|pm)/i.test(dateString);
}

function getNestedPropertyValue(obj, propertyPath) {
  const props = propertyPath.split('.');
  let nestedValue = obj;

  for (let i = 0; i < props.length; i++) {
    if (nestedValue === null || nestedValue === undefined) {
      // If the value is null or undefined, return null (or another default value like an empty string)
      return null;
    }

    const prop = props[i];
    nestedValue = nestedValue[prop];

    if (Array.isArray(nestedValue) && i < props.length - 1) {
      return concatenateNestedValues(nestedValue, props.slice(i + 1).join('.'));
    }
  }

  if (typeof nestedValue === 'string' && isDateString(nestedValue)) {
    nestedValue = parseCustomDateString(nestedValue);
  }

  return nestedValue;
}


export function SortIcon({ sortState, setSortState, setData, setCurrPage, sortingPath, grade = false }) {
  const handleAscending = () => {
    setSortState(true); // Set state to ascending
    setData(prevData => sortAscending([...prevData], sortingPath, grade));
    setCurrPage(1);
  };

  const handleDescending = () => {
    setSortState(false); // Set state to descending
    setData(prevData => sortDescending([...prevData], sortingPath, grade));
    setCurrPage(1);
  };

  return (
    <>
      {sortState === true ? (
        <i
          className="fas fa-sort-amount-up-alt"
          onClick={handleDescending}
          style={{ cursor: 'pointer', marginLeft: '4px' }} // Adding left margin
        ></i>
      ) : sortState === false ? (
        <i
          className="fas fa-sort-amount-down"
          onClick={handleAscending}
          style={{ cursor: 'pointer', marginLeft: '4px' }} // Adding left margin
        ></i>
      ) : (
        <i
          className="fas fa-sort-alt"
          onClick={handleAscending}
          style={{ cursor: 'pointer', marginLeft: '4px' }} // Adding left margin
        ></i>
      )}
    </>
  );
}


{/* <i class="fas fa-sort-alt"></i> */ }






export const searchArray = async (type, apiUrl, searchKeys, searchValue, key) => {
  let data;

  try {
    let response;
    if (type === "A") {
      response = await authorizedAxios.get(apiUrl);
    } else if (type === "I") {
      response = await authorizedAxiosInstitute.get(apiUrl);
    } else if (type === "B") {
      response = await authorizedAxiosSection.get(apiUrl);
    } else if (type === "C") {
      response = await authorizedAxiosCommon.get(apiUrl);
    } else {
      throw new Error('Invalid type specified');
    }

    if (key === 'data') {
      data = response.data.data;
    } else if (key === "rows") {
      data = response.data.result.rows;
    } else if (key === "sections") {
      data = response.data.data.sections;
    } else {
      data = response.data.result;
    }
  } catch (error) {
    const errorMsg = error?.response?.data?.message || 'Something went wrong!';
    Swal.fire({
      text: errorMsg
    });
    return []; // Return an empty array on error
  }


  const getNestedPropertyValue = (obj, propertyPath) => {
    const props = propertyPath.split('.');
    let nestedValue = obj;

    for (let i = 0; i < props.length; i++) {
      const prop = props[i];

      // Return null if nestedValue is null or undefined
      if (nestedValue === null || nestedValue === undefined) {
        return null;
      }

      if (Array.isArray(nestedValue)) {
        // Handle cases where items in the array might be null or undefined
        nestedValue = nestedValue.flatMap(item => (item ? item[prop] : null)).filter(value => value !== null && value !== undefined);
      } else {
        nestedValue = nestedValue[prop];
      }
    }

    return nestedValue;
  };

  return data.filter(item => {
    for (const key of searchKeys) {
      const nestedValue = getNestedPropertyValue(item, key);

      if (nestedValue === null || nestedValue === undefined) {
        continue; // If the value is null or undefined, move on to the next key
      }

      if (Array.isArray(nestedValue)) {
        const concatenatedValue = nestedValue.join(', ');
        if (concatenatedValue.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
      } else if (nestedValue !== undefined && nestedValue !== null) {
        if (typeof nestedValue === 'string') {
          if (nestedValue.toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
        } else {
          if (JSON.stringify(nestedValue).toLowerCase().includes(searchValue.toLowerCase())) {
            return true;
          }
        }
      }
    }
    return false;
  });
};


























export const romanGradeToInt = (grade) => {
  const romanNumerals = {
    'I': 1,
    'IV': 4,
    'V': 5,
    'IX': 9,
    'X': 10,
    'XL': 40,
    'L': 50,
    'XC': 90,
    'C': 100,
    'CD': 400,
    'D': 500,
    'CM': 900,
    'M': 1000
  };

  if (grade === 'LKG') return -2;
  if (grade === 'UKG') return -1;

  let i = 0;
  let num = 0;

  while (i < grade.length) {
    if (i + 1 < grade.length && romanNumerals[grade.substring(i, i + 2)]) {
      num += romanNumerals[grade.substring(i, i + 2)];
      i += 2;
    } else if (romanNumerals[grade.charAt(i)]) {
      num += romanNumerals[grade.charAt(i)];
      i += 1;
    } else {
      return null;
    }
  }

  return num;
}
